import { computed, inject, Injectable, Injector, Signal, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { HubConnection } from '@microsoft/signalr';
import {
    ITransferDisplaySlide,
    ITransferDynamicContentSlide,
    ITransferMinimumSlideInformationList,
    transformDisplaySlideToTransformSlide,
    transformDynamicContentSlideSlideToTransformSlide,
} from '@pcp/streaming';
import { Hotspot, IDisplaySlide, IDynamicContentSlide, SlideType } from '@pcp/core/interfaces';
import { NavigationPreviewNextSlidesTransferPayload } from '../store/classroomState';
import { EMPTY, filter, map, Observable, of, shareReplay, switchMap, startWith } from 'rxjs';
import { Canvas } from 'fabric';
import { BackgroundFit, EphemeralApplyHandler } from 'shared/util-yjs-fabric';
import { type SerializedFabricObject } from 'shared/fabric-drawing/tools';
import { LessonYjsService } from '../../internal/yjs/lesson-yjs.service';
import { LessonDrawingService } from '../../internal/yjs/lesson-drawing.service';
import { DisplayStateSnapshot, parseDeactivatedHotspots, parsePerceptionRevealState, PerceptionRevealState, PrimarySlideHistoryEntry, SyncedLessonState } from './lesson-ydoc-schema';
import { parseDynamicContentItem } from './parse-dynamic-content-item';
import { ActiveElementUiStateService } from '../ui-state/active-element-ui-state.service';
import { shouldKeepViewport } from '../ui-state/util/active-element-keep-viewport';

/** Layer index in the slide-preview component. 1 = primary, 2 = secondary, 3 = tertiary. */
type Layer = 1 | 2 | 3;

/**
 * Handle returned by `ClassroomFacade.attachDrawingCanvas()` for one layer's canvas.
 *
 * The canvas↔Yjs sync (object add/move/remove, late-join restore, reset reconciliation)
 * is automatic once attached — the renderer interacts with the fabric canvas directly and
 * uses this handle only for operations that aren't plain canvas mutations: undo/redo,
 * per-client local background, reset, and the ephemeral (awareness) channel for live
 * in-progress stroke preview. The handle stays valid across the lesson Y.Doc lifecycle
 * (offline↔active, reconnect) — it rebinds internally. Call `detach()` on unmount.
 */
export interface DrawingCanvasHandle {
    undo(): void;
    redo(): void;
    canUndo(): boolean;
    canRedo(): boolean;
    /**
     * Snapshot this layer's exportable drawing objects (serialized fabric JSON, excluding
     * the background and any non-transfer overlays) for client-side image export. Pair with
     * `exportObjectsToBlob` from `shared/fabric-drawing/tools`.
     */
    snapshotObjects(): SerializedFabricObject[];
    /**
     * Apply a background image to this client's canvas only (per-client variant; never synced).
     * @param fit - How to scale the image (default `'fill'`); use `'contain-height'` /
     *   `'contain-width'` for horizontal / vertical panorama slides.
     */
    setBackgroundLocal(url: string | null, fit?: BackgroundFit): void;
    /** Clear all drawing objects on this layer (slide reset) — reconciles every client's canvas. */
    clear(): void;
    /** Publish an ephemeral (awareness) payload — e.g. the local in-progress stroke; pass null to clear. */
    publishEphemeral(field: string, payload: unknown | null): void;
    /** Register a handler for a remote client's ephemeral payload (e.g. render their in-progress stroke). */
    registerEphemeral(field: string, apply: EphemeralApplyHandler): void;
    /**
     * Register (or clear, with `undefined`) a hook fired after a full Yjs reconcile re-adds the
     * synced drawings — used by a content capability to re-place its own (non-synced) objects below
     * the drawings so the first paint has the correct z-order (flicker-free).
     */
    onAfterReconcile(handler: (() => void) | undefined): void;
    /** Tear down this layer's binding and forget the canvas. */
    detach(): void;
}

/**
 * Parses a JSON string into the requested shape, returning `fallback` on null/undefined input
 * or parse error. Stateless — referential stability is provided by Angular's signal graph:
 * the parsed `computed` re-runs only when its raw-string input `computed` changes value.
 */
function parseJsonField<T>(raw: string | null | undefined, fallback: T): T {
    if (raw === null || raw === undefined) return fallback;
    try {
        return JSON.parse(raw) as T;
    } catch {
        return fallback;
    }
}

/**
 * Parse the per-layer dynamicContent Y.Array snapshot (a `string[]` where each
 * entry is a JSON-serialized `ITransferDynamicContentSlide`) into typed items.
 * Items that fail to parse are dropped — defensive against corrupted entries.
 * Delegates per-item parsing to the shared `parseDynamicContentItem` helper so
 * the snapshot and event paths drop corrupted items in the same way.
 */
function parseDynamicContentSnapshot(snapshot: ReadonlyArray<string>): ITransferDynamicContentSlide[] {
    const parsed: ITransferDynamicContentSlide[] = [];
    for (const json of snapshot) {
        const item = parseDynamicContentItem(json);
        if (item !== null) parsed.push(item);
    }
    return parsed;
}

/**
 * ClassroomFacade — single public interface for the classroom subsystem.
 *
 * Wraps the internal `LessonYjsService` and exposes:
 * - State as Observables (gated by hydration so subscribers never see pre-init emissions)
 * - State as Signals where snapshot semantics fit (`hydrated`, `primarySlideHistory`)
 * - All write methods (delegated 1:1 to the service; `guardWrite` enforces permissions)
 * - Lifecycle (`initialize`, `destroy`, `isInitialized`) — only the lifecycle effects should call these
 *
 * Y.Doc is the source of truth. NgRx is a projection driven by `LessonYjsMirrorEffects`,
 * which subscribes to the Observables on this facade.
 */
@Injectable({ providedIn: 'root' })
export class ClassroomFacade {
    private readonly service = inject(LessonYjsService);
    private readonly drawing = inject(LessonDrawingService);
    // Lazy: `ActiveElementUiStateService` injects `ClassroomFacade` to read
    // Y.Doc Signals, so eager construction would deadlock the DI graph.
    private readonly injector = inject(Injector);
    private get uiState(): ActiveElementUiStateService {
        return this.injector.get(ActiveElementUiStateService);
    }

    /**
     * Hotspot latch — decouples a hotspot click from the show-slide that
     * follows it. Set via `latchHotspot()` from the cross-feature event-bus
     * listener that catches `userHasActivatedHotspot`; drained by `showSlide()`
     * via `consumeHotspotLatch()`. Replaces the temporal `pairwise()`
     * correlator that used to live in `active-element.effects.ts`.
     *
     * Semantics: the latch is consumed (cleared) on the next `showSlide`
     * regardless of whether its target matches — this prevents stale latches
     * from leaking across navigation events that don't lead to the target.
     */
    private readonly latchedHotspot = signal<Hotspot | null>(null);

    latchHotspot(hotspot: Hotspot): void {
        this.latchedHotspot.set(hotspot);
    }

    private consumeHotspotLatch(slideId: number | undefined): boolean {
        const current = this.latchedHotspot();
        this.latchedHotspot.set(null);
        return current !== null && current.targetSlideId === slideId;
    }

    /** Emits true once the Y.Doc is ready (initial sync received or defaults written). */
    readonly hydrated$: Observable<boolean> = this.service.hydrated$;
    readonly hydrated: Signal<boolean> = toSignal(this.hydrated$, { initialValue: false });

    /**
     * True iff this client is allowed to write to the Classroom Y.Doc.
     * Role-based and constant per app: only `Teacher` and `Beamer` are writers.
     *
     * Use this to gate UI affordances (buttons, controls) on the writer role
     * instead of guessing from lib placement. Writes themselves are still
     * permission-checked at the service boundary via `guardWrite()`.
     */
    readonly isWriter: Signal<boolean> = this.service.isWriter;

    // --- Drawing canvas (fabric.js migration) ---

    /**
     * Bind a UI-owned fabric canvas to a layer for object-based drawing sync, and return
     * a {@link DrawingCanvasHandle} for undo/redo, per-client background, reset, and the
     * ephemeral stroke-preview channel. Object add/move/remove, late-join restore, and
     * reset reconciliation are automatic once attached. The handle survives the lesson
     * Y.Doc lifecycle (it rebinds internally); call `handle.detach()` on unmount.
     *
     * Writer-role authorization is unchanged: only Teacher/Beamer drawing reaches the
     * Y.Doc (the renderer also gates tool availability on {@link isWriter}).
     */
    attachDrawingCanvas(layer: Layer, canvas: Canvas): DrawingCanvasHandle {
        this.drawing.attachCanvas(layer, canvas);
        return {
            undo: () => this.drawing.undo(layer),
            redo: () => this.drawing.redo(layer),
            canUndo: () => this.drawing.canUndo(layer),
            canRedo: () => this.drawing.canRedo(layer),
            snapshotObjects: () => this.drawing.snapshotObjects(layer),
            setBackgroundLocal: (url, fit) => this.drawing.setBackgroundLocal(layer, url, fit),
            clear: () => this.drawing.clearDrawings(layer),
            publishEphemeral: (field, payload) => this.drawing.publishEphemeral(layer, field, payload),
            registerEphemeral: (field, apply) => this.drawing.registerEphemeral(layer, field, apply),
            onAfterReconcile: (handler) => this.drawing.setAfterReconcile(layer, handler),
            detach: () => this.drawing.detachCanvas(layer),
        };
    }

    /**
     * Y.Map state changes (local + remote). Hydration-gated, with the current snapshot
     * prepended on each (re-)subscription so late-joiners receive the post-sync state.
     */
    readonly changes$: Observable<Partial<SyncedLessonState>> = this.service.hydrated$.pipe(
        switchMap(hydrated => {
            if (!hydrated || !this.service.changes$) return EMPTY;
            const snapshot = this.service.getSnapshot();
            return snapshot !== null
                ? this.service.changes$.pipe(startWith(snapshot))
                : this.service.changes$;
        }),
    );

    /**
     * Full Y.Map state stream. Emits the complete `SyncedLessonState` on every Y.Doc change
     * (read fresh from `getSnapshot()` after the change has been applied). Hydration-gated
     * — never emits before the Y.Doc is ready.
     *
     * Use this when a consumer needs the whole state on every change. Use `changes$` when
     * only the changed keys matter.
     *
     * Shared (`shareReplay({ bufferSize: 1, refCount: true })`): `getSnapshot()` walks the
     * 29-key Y.Map per emission — running it once for all subscribers (instead of once per
     * subscriber) is a real saving in the per-mutation hot path. New subscribers get the
     * latest snapshot from the replay buffer; when all subscribers leave the share resets,
     * so a fresh connection (e.g. after lesson restart) re-flows from `changes$`.
     */
    readonly state$: Observable<SyncedLessonState> = this.changes$.pipe(
        map(() => this.service.getSnapshot()),
        filter((s): s is SyncedLessonState => s !== null),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    /**
     * `state$` as a Signal, with one deliberate difference: it drops to `null`
     * while no hydrated document exists. `state$` simply stops emitting then (its
     * gate switches to `EMPTY`), which is right for a stream but would leave a
     * Signal *holding* the destroyed document's last snapshot — after a
     * session-ending `destroy()` (see `discardOnLogout$`) every display Signal
     * derived from this would keep rendering the previous session's lesson. The
     * spec scenario is "a logged-out client displays no lesson content", so the
     * data layer resets rather than relying on routing to unmount consumers.
     */
    readonly state: Signal<SyncedLessonState | null> = toSignal(
        this.service.hydrated$.pipe(
            switchMap(hydrated => hydrated ? this.state$ : of(null)),
        ),
        { initialValue: null },
    );

    /**
     * True iff a lesson has been started (status is anything other than `'draft'`).
     *
     * - `false` when the Y.Doc is uninitialized OR in offline/draft mode.
     * - `true` when the Y.Doc has been initialized for a real lesson — status will be
     *   `'active'` (the only non-`'draft'` value any writer produces; see the
     *   `LessonStatus` docstring in `lesson-ydoc-schema.ts`).
     *
     * Use this from UI / effects to gate behavior that should only run during a real
     * lesson (e.g. SignalR-dependent commands). Display logic should NOT branch on
     * this — the rendering path is identical pre- and post-lesson-start.
     */
    readonly isLessonActive: Signal<boolean> = computed(() => {
        const s = this.state();
        return s !== null && s.lessonStatus !== 'draft';
    });

    /** Remote DomMutation batches added to a layer's Y.Array. Hydration-gated; no snapshot prepend (events, not state). */
    readonly mutationBatchAdded$: Observable<{ layer: 1 | 2 | 3; batch: string }> = this.service.hydrated$.pipe(
        switchMap(hydrated => (hydrated && this.service.mutationBatchAdded$) ? this.service.mutationBatchAdded$ : EMPTY),
    );

    /**
     * `clearMutations()` events that emptied a layer's Y.Array. Hydration-gated;
     * no snapshot prepend (events, not state). Symmetric on writer + receiver
     * (task 14, 2026-05-15): the `transaction.local` skip in the binding observer
     * is scoped to the `delta.insert` branch only, so a local clear fires the
     * event for the writer's own slide-layer teardown. Suppressed when the same
     * transaction also writes a `<layer>Slide` identity field (`setSlide` /
     * `removeSlide` cascade clears).
     *
     * See `.claude/skills/yjs-architecture/references/reset-dynamic-content.md` for
     * the full reset workflow.
     */
    readonly mutationBatchesCleared$: Observable<{ layer: 1 | 2 | 3 }> = this.service.hydrated$.pipe(
        switchMap(hydrated => (hydrated && this.service.mutationBatchesCleared$) ? this.service.mutationBatchesCleared$ : EMPTY),
    );

    /**
     * Dynamic-content items appended to a layer's Y.Array. Hydration-gated;
     * symmetric on writer + receiver (unlike `mutationBatchAdded$`, which
     * suppresses local inserts). Items are JSON-parsed at the facade boundary;
     * unparseable items are dropped.
     *
     * Late-join replay: consumers that need the full backlog must read
     * `getDynamicContentSnapshot(layer)` themselves at subscribe time (see
     * the `defer + merge(snapshot, events)` pattern in
     * `slide-preview.facade.ts:createAppendDynamicContent$`). This Observable
     * emits ONLY items added after subscription.
     */
    readonly dynamicContentAdded$: Observable<{ layer: 1 | 2 | 3; item: ITransferDynamicContentSlide }> =
        this.service.hydrated$.pipe(
            switchMap(hydrated => (hydrated && this.service.dynamicContentAdded$) ? this.service.dynamicContentAdded$ : EMPTY),
            map(({ layer, itemJson }) => ({ layer, item: parseDynamicContentItem(itemJson) })),
            filter((e): e is { layer: 1 | 2 | 3; item: ITransferDynamicContentSlide } => e.item !== null),
        );

    /**
     * `clearDynamicContent()` events that emptied a layer's Y.Array. Hydration-gated.
     * Symmetric on writer + receiver; suppressed on slide-identity cascades
     * (`setSlide` / `removeSlide`). Fires on `resetSlideDynamicContent()` and
     * `resetSlideToInitial()`.
     */
    readonly dynamicContentCleared$: Observable<{ layer: 1 | 2 | 3 }> = this.service.hydrated$.pipe(
        switchMap(hydrated => (hydrated && this.service.dynamicContentCleared$) ? this.service.dynamicContentCleared$ : EMPTY),
    );

    /** Primary-slide history snapshots (full array on each change). Hydration-gated, with current snapshot prepended on subscribe. */
    readonly primarySlideHistory$: Observable<PrimarySlideHistoryEntry[]> = this.service.hydrated$.pipe(
        switchMap(hydrated => {
            if (!hydrated || !this.service.primarySlideHistory$) return EMPTY;
            return this.service.primarySlideHistory$.pipe(startWith(this.service.getPrimarySlideHistorySnapshot()));
        }),
    );

    /** Signal variant; resets to `[]` while no hydrated document exists (same rationale as `state`). */
    readonly primarySlideHistory: Signal<PrimarySlideHistoryEntry[]> = toSignal(
        this.service.hydrated$.pipe(
            switchMap(hydrated => hydrated ? this.primarySlideHistory$ : of([] as PrimarySlideHistoryEntry[])),
        ),
        { initialValue: [] as PrimarySlideHistoryEntry[] },
    );

    /**
     * Tick that increments on every dynamic-content Y.Array event (add or
     * cleared) on any layer. Each per-layer `*DynamicContentRaw` Signal reads
     * the tick so it re-evaluates when the underlying Y.Array changes; reading
     * `getDynamicContentSnapshot(layer)` then returns the fresh items.
     *
     * Shared across layers for simplicity — a layer-2 write invalidates layer-1
     * and layer-3 raw computeds too, but their `getDynamicContentSnapshot()`
     * return value is unchanged, so by `Object.is` the parsed computed sees no
     * change and downstream consumers don't churn.
     */
    private readonly dynamicContentTick = signal(0);

    constructor() {
        // Drive the dynamicContent tick from binding-level events. Hydration-gated
        // streams reattach automatically across `destroy()` / `initialize()` cycles.
        this.dynamicContentAdded$.subscribe(() => this.dynamicContentTick.update(t => t + 1));
        this.dynamicContentCleared$.subscribe(() => this.dynamicContentTick.update(t => t + 1));
    }

    // --- Per-layer derived Signals ---
    //
    // Typed projections of the JSON-stringified Y.Doc layer fields. Each layer (primary,
    // secondary, tertiary) exposes:
    //   - `<layer>Slide`                    parsed `ITransferDisplaySlide | null`
    //   - `<layer>SlideIsAccessedViaHotspot` boolean
    //   - `<layer>SlideViewBox`             string
    //   - `<layer>SlideTextboardPage`       number
    //   - `<layer>SlideDynamicContent`      parsed `ITransferDynamicContentSlide[]` (default `[]`)
    //
    // Shape: `ITransferDisplaySlide` (NOT `IDisplaySlide`) — Y.Doc stores the transfer
    // shape (`Omit<IDisplaySlide, 'imageVariants' | 'previewpicture'>`); `imageVariants` is
    // never reconstructed on the read side. Exactly one image URL is synced per slide;
    // clients that need a specific quality variant derive it locally by URL convention
    // (`swapImageQualityInUrl` in `@pcp/streaming`).
    //
    // DynamicContent lives in a sibling Y.Array (not in the Y.Map) — see the
    // `dynamicContentTick` signal above and `parseDynamicContentSnapshot()` below.
    //
    // Memoization (two-level): each layer has a private raw-string `computed` that reads
    // its specific Y.Doc field, plus a public parsed `computed` that depends on the raw.
    // When `state()` changes due to ANY field, the raw `computed` re-evaluates but its
    // output (a string) only "changes" (per `Object.is`) when its specific field changed.
    // The parsed `computed` therefore JSON-parses at most once per Y.Doc emission of THAT
    // layer's field, and downstream consumers get referentially-stable parsed objects
    // until the underlying string actually changes. (Layer isolation: writes to secondary
    // do not invalidate primary's parsed value.)

    private readonly primarySlideRaw = computed(() => this.state()?.primarySlide ?? null);
    readonly primarySlide: Signal<ITransferDisplaySlide | null> = computed(() =>
        parseJsonField<ITransferDisplaySlide | null>(this.primarySlideRaw(), null)
    );
    readonly primarySlideIsAccessedViaHotspot: Signal<boolean> = computed(
        () => this.state()?.primarySlideIsAccessedViaHotspot ?? false
    );
    readonly primarySlideViewBox: Signal<string | undefined> = computed(
        () => this.state()?.primarySlideViewBox
    );
    readonly primarySlideTextboardPage: Signal<number | undefined> = computed(
        () => this.state()?.primarySlideTextboardPage
    );
    private readonly primarySlideDynamicContentRaw = computed(() => {
        this.dynamicContentTick();
        // A slide change clears this layer's dynamic content in the same transaction that writes the
        // new slide, and that cascade SUPPRESSES the `dynamicContentCleared$` event (so it never bumps
        // the tick). Depend on the slide identity too, so the snapshot is re-read (now empty) on any
        // slide change — otherwise the signal returns the stale pre-change list.
        this.primarySlideRaw();
        return this.service.getDynamicContentSnapshot(1);
    });
    readonly primarySlideDynamicContent: Signal<ITransferDynamicContentSlide[]> = computed(() =>
        parseDynamicContentSnapshot(this.primarySlideDynamicContentRaw())
    );

    private readonly secondarySlideRaw = computed(() => this.state()?.secondarySlide ?? null);
    readonly secondarySlide: Signal<ITransferDisplaySlide | null> = computed(() =>
        parseJsonField<ITransferDisplaySlide | null>(this.secondarySlideRaw(), null)
    );
    readonly secondarySlideIsAccessedViaHotspot: Signal<boolean> = computed(
        () => this.state()?.secondarySlideIsAccessedViaHotspot ?? false
    );
    readonly secondarySlideViewBox: Signal<string | undefined> = computed(
        () => this.state()?.secondarySlideViewBox
    );
    readonly secondarySlideTextboardPage: Signal<number | undefined> = computed(
        () => this.state()?.secondarySlideTextboardPage
    );
    private readonly secondarySlideDynamicContentRaw = computed(() => {
        this.dynamicContentTick();
        this.secondarySlideRaw(); // recompute on slide change (suppressed clear cascade) — see primary
        return this.service.getDynamicContentSnapshot(2);
    });
    readonly secondarySlideDynamicContent: Signal<ITransferDynamicContentSlide[]> = computed(() =>
        parseDynamicContentSnapshot(this.secondarySlideDynamicContentRaw())
    );

    private readonly tertiarySlideRaw = computed(() => this.state()?.tertiarySlide ?? null);
    readonly tertiarySlide: Signal<ITransferDisplaySlide | null> = computed(() =>
        parseJsonField<ITransferDisplaySlide | null>(this.tertiarySlideRaw(), null)
    );
    readonly tertiarySlideIsAccessedViaHotspot: Signal<boolean> = computed(
        () => this.state()?.tertiarySlideIsAccessedViaHotspot ?? false
    );
    readonly tertiarySlideViewBox: Signal<string | undefined> = computed(
        () => this.state()?.tertiarySlideViewBox
    );
    readonly tertiarySlideTextboardPage: Signal<number | undefined> = computed(
        () => this.state()?.tertiarySlideTextboardPage
    );
    private readonly tertiarySlideDynamicContentRaw = computed(() => {
        this.dynamicContentTick();
        this.tertiarySlideRaw(); // recompute on slide change (suppressed clear cascade) — see primary
        return this.service.getDynamicContentSnapshot(3);
    });
    readonly tertiarySlideDynamicContent: Signal<ITransferDynamicContentSlide[]> = computed(() =>
        parseDynamicContentSnapshot(this.tertiarySlideDynamicContentRaw())
    );

    // --- Perception-image reveal display-state (canvas-image-slides) ---
    //
    // Per-layer `{ epoch, revealed }` parsed from the `<layer>SlideRevealState` Y.Doc field.
    // `revealed` is the source of truth for whether the layer's perception image is pinned
    // visible; it resets on every show / content-reset (per-showing, not lesson-wide). `epoch`
    // bumps on `resetSlideToInitial` so remote clients re-run the hide animation on a same-slide
    // reset even when it was not yet revealed. Two-level memoization mirrors the other layer
    // signals: a raw-string computed feeds the parsed computed so unrelated writes don't churn.

    private readonly primarySlideRevealStateRaw = computed(() => this.state()?.primarySlideRevealState ?? null);
    readonly primarySlideRevealState: Signal<PerceptionRevealState> = computed(() =>
        parsePerceptionRevealState(this.primarySlideRevealStateRaw())
    );

    private readonly secondarySlideRevealStateRaw = computed(() => this.state()?.secondarySlideRevealState ?? null);
    readonly secondarySlideRevealState: Signal<PerceptionRevealState> = computed(() =>
        parsePerceptionRevealState(this.secondarySlideRevealStateRaw())
    );

    private readonly tertiarySlideRevealStateRaw = computed(() => this.state()?.tertiarySlideRevealState ?? null);
    readonly tertiarySlideRevealState: Signal<PerceptionRevealState> = computed(() =>
        parsePerceptionRevealState(this.tertiarySlideRevealStateRaw())
    );

    /** Read the perception reveal-state signal for a given layer. */
    revealStateForLayer(layer: Layer): Signal<PerceptionRevealState> {
        switch (layer) {
            case 1: return this.primarySlideRevealState;
            case 2: return this.secondarySlideRevealState;
            case 3: return this.tertiarySlideRevealState;
        }
    }

    // --- Per-layer showing identity ---
    //
    // `<layer>SlideShowingId` is minted fresh by `resetLayerForNewShowing` at EVERY fresh-showing
    // transition — `setSlide`, `removeSlide` and `resetSlideToInitial` alike — inside the same
    // transaction as the slide identity it belongs to.
    //
    // It is the only unconditional "this layer started a new showing" signal. The `*Cleared$`
    // observables are not: they fire only when a clear actually emptied a non-empty Y.Array, so
    // they stay silent for a slide whose visual state lives outside the Y.Doc (a rubber element's
    // erase strokes are SignalR-ephemeral, a video's playback position is in the player). Consumers
    // driving a UI teardown must key off this, not off the cleared events.

    readonly primarySlideShowingId: Signal<string> = computed(() => this.state()?.primarySlideShowingId ?? '');
    readonly secondarySlideShowingId: Signal<string> = computed(() => this.state()?.secondarySlideShowingId ?? '');
    readonly tertiarySlideShowingId: Signal<string> = computed(() => this.state()?.tertiarySlideShowingId ?? '');

    /** Read the showing-id signal for a given layer. */
    showingIdForLayer(layer: Layer): Signal<string> {
        switch (layer) {
            case 1: return this.primarySlideShowingId;
            case 2: return this.secondarySlideShowingId;
            case 3: return this.tertiarySlideShowingId;
        }
    }

    // Per-layer synced hotspot-deactivation set (D6/C14) — the set of hotspot indices the teacher has
    // activated-and-consumed. Two-level memoization like the reveal-state signals: a raw-string
    // computed feeds the parsed Set so unrelated writes don't churn. The renderer projects this
    // (dim + skip hit-test) instead of tracking deactivation locally.

    private readonly primarySlideDeactivatedHotspotsRaw = computed(() => this.state()?.primarySlideDeactivatedHotspots ?? null);
    readonly primarySlideDeactivatedHotspots: Signal<ReadonlySet<number>> = computed(() =>
        new Set(parseDeactivatedHotspots(this.primarySlideDeactivatedHotspotsRaw()))
    );

    private readonly secondarySlideDeactivatedHotspotsRaw = computed(() => this.state()?.secondarySlideDeactivatedHotspots ?? null);
    readonly secondarySlideDeactivatedHotspots: Signal<ReadonlySet<number>> = computed(() =>
        new Set(parseDeactivatedHotspots(this.secondarySlideDeactivatedHotspotsRaw()))
    );

    private readonly tertiarySlideDeactivatedHotspotsRaw = computed(() => this.state()?.tertiarySlideDeactivatedHotspots ?? null);
    readonly tertiarySlideDeactivatedHotspots: Signal<ReadonlySet<number>> = computed(() =>
        new Set(parseDeactivatedHotspots(this.tertiarySlideDeactivatedHotspotsRaw()))
    );

    /** Read the synced deactivated-hotspot-index set signal for a given layer. */
    deactivatedHotspotsForLayer(layer: Layer): Signal<ReadonlySet<number>> {
        switch (layer) {
            case 1: return this.primarySlideDeactivatedHotspots;
            case 2: return this.secondarySlideDeactivatedHotspots;
            case 3: return this.tertiarySlideDeactivatedHotspots;
        }
    }

    // Per-layer settled scroll-video frame index (`canvas-scroll-video-slides`): `null` until the
    // writer's scrubbing settles (display falls back to the slide's seed frame, `current ?? start`).
    // Late-join / refresh restore from this field; the LIVE scrub stream is ephemeral (awareness)
    // and never surfaces here.

    readonly primarySlideScrollVideoFrame: Signal<number | null> = computed(
        () => this.state()?.primarySlideScrollVideoFrame ?? null
    );
    readonly secondarySlideScrollVideoFrame: Signal<number | null> = computed(
        () => this.state()?.secondarySlideScrollVideoFrame ?? null
    );
    readonly tertiarySlideScrollVideoFrame: Signal<number | null> = computed(
        () => this.state()?.tertiarySlideScrollVideoFrame ?? null
    );

    /** Read the settled scroll-video frame-index signal for a given layer. */
    scrollVideoFrameForLayer(layer: Layer): Signal<number | null> {
        switch (layer) {
            case 1: return this.primarySlideScrollVideoFrame;
            case 2: return this.secondarySlideScrollVideoFrame;
            case 3: return this.tertiarySlideScrollVideoFrame;
        }
    }

    /** Read the synced dynamic-content list signal for a given layer (textboard / graphic overlays). */
    dynamicContentForLayer(layer: Layer): Signal<ITransferDynamicContentSlide[]> {
        switch (layer) {
            case 1: return this.primarySlideDynamicContent;
            case 2: return this.secondarySlideDynamicContent;
            case 3: return this.tertiarySlideDynamicContent;
        }
    }

    /**
     * Topmost non-null slide across all active layers, selecting in order: tertiary → secondary → primary.
     * Use when the UI shows a single "current" slide (e.g. preview pane).
     *
     * Layer hierarchy:
     * - Tertiary (top): e.g. fullscreen popup
     * - Secondary (middle): e.g. picture-in-picture / footnote
     * - Primary (bottom): main lesson slide
     */
    readonly currentLayerSlide: Signal<ITransferDisplaySlide | null> = computed(
        () => this.tertiarySlide() ?? this.secondarySlide() ?? this.primarySlide()
    );

    // --- Signal indexer methods (return signals keyed by layer) ---

    /**
     * Read the slide signal for a given layer. Useful for code paths that index by layer
     * (e.g. `forEach([1, 2, 3], ...)`) rather than referencing a specific layer signal.
     */
    slideForLayer(layer: Layer): Signal<ITransferDisplaySlide | null> {
        switch (layer) {
            case 1: return this.primarySlide;
            case 2: return this.secondarySlide;
            case 3: return this.tertiarySlide;
        }
    }

    /**
     * Read the viewBox signal for a given layer.
     */
    viewBoxForLayer(layer: Layer): Signal<string | undefined> {
        switch (layer) {
            case 1: return this.primarySlideViewBox;
            case 2: return this.secondarySlideViewBox;
            case 3: return this.tertiarySlideViewBox;
        }
    }

    /**
     * Read the textboardPage signal for a given layer.
     */
    textboardPageForLayer(layer: Layer): Signal<number | undefined> {
        switch (layer) {
            case 1: return this.primarySlideTextboardPage;
            case 2: return this.secondarySlideTextboardPage;
            case 3: return this.tertiarySlideTextboardPage;
        }
    }

    // --- Snapshot reads (synchronous) ---

    getSnapshot(): SyncedLessonState | null {
        return this.service.getSnapshot();
    }

    getMutationBatches(layer: Layer): string[] {
        return this.service.getMutationBatches(layer);
    }

    /** Snapshot of the per-layer dynamicContent Y.Array. Raw JSON strings. */
    getDynamicContentSnapshot(layer: Layer): string[] {
        return this.service.getDynamicContentSnapshot(layer);
    }

    getPrimarySlideHistorySnapshot(): PrimarySlideHistoryEntry[] {
        return this.service.getPrimarySlideHistorySnapshot();
    }

    // --- Lifecycle (called by `LessonYjsLifecycleEffects` only) ---

    initialize(
        lessonId: string,
        hub: HubConnection,
        opts?: {
            isFreshLesson?: boolean;
            resignUrls?: (snapshot: DisplayStateSnapshot) => DisplayStateSnapshot;
            awarenessSendEnabled$?: Observable<boolean>;
        },
    ): Promise<void> {
        return this.service.initialize(lessonId, hub, opts);
    }

    /**
     * Create a local-only Y.Doc with no SignalR provider. See `LessonYjsService.initializeOffline`.
     * Used pre-lesson so display reads/writes go through the same Y.Doc-backed path as a live lesson,
     * and again on lesson stop with a `seed` — the lesson → draft display-state handoff. The caller
     * must export the snapshot BEFORE calling (this destroys the current document internally).
     */
    initializeOffline(
        seed?: DisplayStateSnapshot,
        resignUrls?: (snapshot: DisplayStateSnapshot) => DisplayStateSnapshot,
    ): void {
        this.service.initializeOffline(seed, resignUrls);
    }

    /**
     * Export the current document's display state for a handoff — everything except
     * `LESSON_SCOPED_KEYS` and the primary-slide history (see
     * `LessonYjsBinding.exportDisplayState`). `null` when no document exists.
     * Only the lifecycle effects should call this (the stop effect exports before
     * `destroy()` + `initializeOffline(snapshot)`).
     */
    exportDisplayState(): DisplayStateSnapshot | null {
        return this.service.exportDisplayState();
    }

    destroy(): void {
        this.service.destroy();
    }

    isInitialized(): boolean {
        return this.service.isInitialized();
    }

    /**
     * Whether the current document is a synced document FOR the given lesson.
     * `false` in offline (`'draft'`) mode — a draft document is "initialized" but
     * NOT restored/synced — and `false` when the document belongs to a different
     * lesson. Use this (not `isInitialized()`) to decide whether a lesson restore
     * is still needed: a leftover document from an earlier lesson must not
     * suppress initialization for the lesson being joined.
     */
    isSyncedToLesson(lessonId: string): boolean {
        return this.service.isSyncedToLesson(lessonId);
    }

    // --- Write methods ---
    //
    // Lesson-lifecycle and slide-domain writes. Names follow lesson-domain vocabulary
    // (`showSlide`, `panSlide`, `flipPage`, `appendDynamicContent`, `closeLayer`)
    // where the underlying Y.Doc field has a less obvious name. Raw Y.Doc setter
    // methods (`setSlide`, `setSlideViewBox`, etc.) have been dropped from the
    // public facade — call sites use the semantic methods instead.

    pauseLesson(): void { this.service.pauseLesson(); }
    resumeLesson(): void { this.service.resumeLesson(); }
    setPauseImageUrl(url: string | null): void { this.service.setPauseImageUrl(url); }
    beginLessonStop(): void { this.service.beginLessonStop(); }
    endLessonStop(): void { this.service.endLessonStop(); }
    openExtensionApp(extensionApp: string): void { this.service.openExtensionApp(extensionApp); }
    closeExtensionApp(): void { this.service.closeExtensionApp(); }

    /**
     * Show / hide the student registration code on the beamer by toggling the synced
     * `isShowingStudentRegistrationCode` flag. Store-less state writes (no dispatch) — the
     * sanctioned way a classroom feature lib mutates this state, since the internal Y.Doc
     * service is unexported. Callers own the writer check (`isWriter`); the service `guardWrite`
     * only checks Y.Doc init, not role. See the `student-registration-code` feature in
     * `shared/classroom/features` for the trigger + auto-hide policy that drives these.
     */
    showRegistrationCode(): void { this.service.showRegistrationCode(); }
    hideRegistrationCode(): void { this.service.hideRegistrationCode(); }

    appendMutationBatch(layer: Layer, batchJson: string): void { this.service.appendMutationBatch(layer, batchJson); }

    /**
     * Reveal the perception image on a layer (pins it visible; source of truth for "revealed",
     * see {@link revealStateForLayer}). The reset back to hidden happens automatically in the
     * `showSlide` / `resetSlideToInitial` cascades — there is no un-reveal. Writer-role enforced
     * at the service boundary.
     */
    revealPerceptionSlide(layer: Layer): void { this.service.revealPerceptionSlide(layer); }

    /**
     * Deactivate a hotspot on a layer (D6/C14): add its index (into the slide's filtered
     * supported-hotspot list) to the synced deactivated-set so it dims + stops hit-testing on every
     * client. Idempotent. The re-arm back to empty happens via the `showSlide` / `removeSlide` /
     * `resetSlideToInitial` cascades. The caller gates on `isWriter` (Teacher/Beamer) — like pan; the
     * service `guardWrite` only checks Y.Doc init, not role.
     */
    deactivateHotspot(layer: Layer, index: number): void { this.service.deactivateHotspot(layer, index); }

    // --- Semantic write API (preferred) ---

    /**
     * Close a secondary or tertiary layer (clears its slide; lower layers remain).
     * Semantic alias of `removeSlide`.
     */
    closeLayer(layer: 2 | 3): void { this.service.removeSlide(layer); }

    /**
     * Pan a panorama slide on the given layer to the supplied viewBox.
     * Semantic alias of `setSlideViewBox` (the underlying field is named after
     * its storage shape; the operation it represents is panning).
     */
    panSlide(layer: Layer, viewBox: string): void { this.service.setSlideViewBox(layer, viewBox); }

    /**
     * Flip a textboard slide on the given layer to the supplied page number.
     * Semantic alias of `setSlideTextboardPage`.
     */
    flipPage(layer: Layer, pageNumber: number): void { this.service.setSlideTextboardPage(layer, pageNumber); }

    /**
     * Persist the settled scroll-video frame index for a layer (`canvas-scroll-video-slides`).
     * Semantic alias of `setSlideScrollVideoFrame`. Call debounced when scrubbing settles — the
     * per-tick live stream rides the drawing handle's ephemeral channel, not the Y.Doc. The reset
     * back to the seed frame (`null`) happens via the `showSlide` / `closeLayer` /
     * `resetSlideToInitial` cascades. The caller gates on `isWriter` — like pan.
     */
    scrubScrollVideo(layer: Layer, frame: number): void { this.service.setSlideScrollVideoFrame(layer, frame); }

    /**
     * Reset the dynamic content array on the given layer to empty.
     * Semantic alias of `resetSlideDynamicContent`.
     *
     * Narrow scope: only clears `<layer>SlideDynamicContent`. Does NOT clear
     * mutations, viewBox, or textboardPage. For the workspace "user pressed
     * reset" gesture (same-slide re-click), use `resetSlideToInitial()` —
     * see `.claude/skills/yjs-architecture/references/reset-dynamic-content.md`.
     */
    resetDynamicContent(layer: Layer): void { this.service.resetSlideDynamicContent(layer); }

    /**
     * Reset a layer back to its "slide just opened" state without changing the
     * slide identity. In one Y.Doc transaction:
     *
     *  - `<layer>SlideDynamicContent` → `'[]'`
     *  - `<layer>SlideViewBox` → default `'0 0 1280 720'`
     *  - `<layer>SlideTextboardPage` → `1`
     *  - Mutation Y.Array for the layer → cleared
     *
     * This is the write surface for the workspace same-slide re-click gesture
     * (the "reset to initial state" gesture). Matches `setSlide()`'s reset
     * payload, minus the slide-identity write. Remote clients react via the
     * Signal updates and `mutationBatchesCleared$`. See
     * `.claude/skills/yjs-architecture/references/reset-dynamic-content.md`
     * for the full workflow.
     */
    resetSlideToInitial(layer: Layer): void { this.service.resetSlideToInitial(layer); }

    /**
     * Append a single dynamic-content slide to the given layer's array.
     *
     * Accepts the display-shape `IDynamicContentSlide` (with `imageVariants`)
     * and handles the storage transform (`transformDynamicContentSlideSlideToTransformSlide`
     * to strip `imageVariants`, plus `JSON.stringify`) internally. Call sites
     * therefore don't need to know about the transfer-shape boundary.
     *
     * Replaces the inline transform currently at workspace
     * `preview-screen.effects.ts:appendDynamicContentSlide$`.
     *
     * Note: bullet-point construction (`SlideDetailsCreator.text(...)`) remains
     * a workspace concern — the classroom facade only accepts the constructed
     * slide. The synthesis' `appendBulletPoint(layer, text)` method is
     * intentionally NOT added here because its slide-factory dependency
     * (`SlideDetailsCreator`) lives in `teacher/shared` and the classroom
     * facade is `scope:shared`. Workspace effects can do:
     *   `lessonStateFacade.appendDynamicContent(2, createSlideDetails.text(text))`
     * which is one line longer but keeps the slide-vocabulary in its proper
     * domain.
     */
    appendDynamicContent(layer: Layer, slide: IDynamicContentSlide): void {
        this.service.appendSlideDynamicContent(
            layer,
            JSON.stringify(transformDynamicContentSlideSlideToTransformSlide(slide)),
        );
    }

    /**
     * Set the navigation preview's basic-slide list for beamer/student display.
     *
     * Accepts the wire-shape transfer payload (already mapped to
     * `ITransferMinimumSlideInformationList` by the caller via
     * `*AsITransferMinimumSlideInformation$` selectors) and serializes it for
     * the Y.Doc `navigationPreviewBasicSlides` field. Absorbs the call-site
     * `JSON.stringify(basicSlideList)` idiom currently at
     * `preview-screen.effects.ts:sendPreviewForTheCurrentChapterToBeamer$`.
     */
    setNavigationPreviewForBasicSlides(payload: ITransferMinimumSlideInformationList): void {
        this.service.setNavigationPreviewBasicSlides(JSON.stringify(payload));
    }

    /**
     * Set the navigation preview's next-slides context for beamer/student display.
     *
     * Accepts the wire-shape transfer payload — the receive side rehydrates
     * thumbnail URLs and maps `extensionSlideList` back to `extensionSlides`
     * (see `classroom-consumer.effects.ts:onYjsNextSlidesChanged$`). `null`
     * clears the preview. Absorbs the call-site reshape + `JSON.stringify`
     * currently at `preview-screen.effects.ts:sendPreviewForNextSlidesToBeamer$`.
     */
    setNavigationPreviewForNextSlides(payload: NavigationPreviewNextSlidesTransferPayload | null): void {
        this.service.setNavigationPreviewNextSlides(JSON.stringify(payload));
    }

    /**
     * Public entry point for showing a slide on a layer. Single semantic
     * write that does:
     *
     *  - Consume the hotspot latch (unless the caller asserts `opts.source ===
     *    'hotspot'` explicitly, in which case the flag is trusted and the
     *    latch is left alone).
     *  - Capture the previous slide + effective viewBox BEFORE the Y.Doc
     *    write so the `shouldKeepViewport` pair table can decide whether to
     *    carry the viewBox into the new slide. After `setSlide`, the layer's
     *    viewBox field is reset to default; reading it afterward yields the
     *    wrong value.
     *  - Delegate the Y.Doc write to the internal `showSlideOnLayer`.
     *
     * No-op when `slide` is `null` — some upstream effects forward optional
     * slides through, so the public method tolerates that rather than
     * making every caller filter first.
     */
    showSlide(
        layer: Layer,
        slide: IDisplaySlide | null,
        opts?: { source?: 'hotspot'; lectionId?: number | null },
    ): void {
        if (slide === null) return;

        const isAccessedViaHotspot = opts?.source === 'hotspot'
            || this.consumeHotspotLatch(slide.id);

        const prevTransfer = this.slideForLayer(layer)();
        const prevSlide = prevTransfer as unknown as IDisplaySlide | null;
        const effectiveViewBox = this.uiState.effectiveViewBoxForLayer(layer)();
        const keptViewBox = prevSlide && shouldKeepViewport(prevSlide, slide)
            ? effectiveViewBox
            : undefined;

        this.showSlideOnLayer(layer, slide, {
            source: isAccessedViaHotspot ? 'hotspot' : undefined,
            keptViewBox,
            lectionId: opts?.lectionId ?? null,
        });
    }

    /**
     * Internal Y.Doc-write helper. Public callers should use `showSlide()`,
     * which absorbs the latch-consume / keep-viewport orchestration. This
     * method does only the synchronous Y.Doc transaction:
     *
     *  - the display-shape → transfer-shape transform + JSON.stringify
     *  - the primary-slide history entry (skipped for `SlideType.empty`
     *    sentinel slides, which exist only to clear the beamer display and
     *    would fail server-side history validation)
     *  - the panorama initial-viewBox write that runs *after* `setSlide`
     *    (which resets the layer's viewBox field to the default `'0 0 1280 720'`)
     *
     * VIEWBOX DECISION TREE:
     *  - Non-panorama slide → no viewBox write (default is correct).
     *  - Panorama + `keptViewBox` provided → write `keptViewBox`.
     *  - Panorama + `initialViewport > 0` → derive
     *    `${N} 0 1280 720` (horizontal) or `0 ${N} 1280 720` (vertical).
     *  - Panorama + `initialViewport === 0` → no viewBox write.
     */
    showSlideOnLayer(
        layer: Layer,
        slide: IDisplaySlide,
        opts?: { source?: 'hotspot'; keptViewBox?: string; lectionId?: number | null },
    ): void {
        const isAccessedViaHotspot = opts?.source === 'hotspot';
        const includeHistory = layer === 1 && slide.type !== SlideType.empty;

        this.service.setSlide(
            layer,
            JSON.stringify(transformDisplaySlideToTransformSlide(slide)),
            isAccessedViaHotspot,
            includeHistory ? {
                moduleId: slide.moduleId,
                versionId: slide.versionId,
                // Resolved by the caller (teacher scope) at navigation time — this lib has no
                // module-structure knowledge. `null` means "not attributable"; such entries are
                // recorded but excluded from the server's per-lection accumulation.
                lectionId: opts?.lectionId ?? null,
                slideId: slide.id,
                shownAt: new Date().toISOString(),
            } : undefined,
        );

        this.writeInitialViewBoxAfterSetSlide(layer, slide, opts?.keptViewBox);
    }

    private writeInitialViewBoxAfterSetSlide(
        layer: Layer,
        slide: IDisplaySlide,
        keptViewBox: string | undefined,
    ): void {
        const panorama = slide.panorama;
        if (!panorama) return;

        if (keptViewBox !== undefined) {
            this.service.setSlideViewBox(layer, keptViewBox);
            return;
        }

        const initial = panorama.initialViewport ?? 0;
        if (initial === 0) return;
        const viewBox = panorama.direction === 'Vertical'
            ? `0 ${initial} 1280 720`
            : `${initial} 0 1280 720`;
        this.service.setSlideViewBox(layer, viewBox);
    }

    // NOTE: the facade deliberately exposes NO event Observables. Classroom
    // events are public NgRx actions (`ClassroomEvents`, T1 tier) consumed via
    // `ofType(...)`; facade Observables carry exactly one meaning — "the
    // synced document changed" (see the Y.Doc change streams above). The
    // previous `hotspotActivated$` / `addedToFavorites$` wrappers had zero
    // subscribers and were removed with the ClassroomEvents promotion
    // (docs/architecture-review-classroom.md, R3/F3).
}
